import { IconButton, Menu, MenuItem } from '@mui/material'
import type { ChangeEvent } from 'react'
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useSetState } from 'react-use'
import FluentAdd20Regular from '~icons/fluent/add-20-regular'
import IcBaselineMoreVert from '~icons/ic/baseline-more-vert'
import { UploadImagePlacerholder } from 'src/components/GenerationModel/GenerationModelEdit/ModelSubmitForms/ModelAssetForm'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import type { Bookmark, Media } from 'src/services/apollo/generated/graphql'
import {
  createCollection,
  isDefaultCollection,
  removeCollection,
  updateCollection,
  useCollections,
} from 'src/services/helpers/bookmarkCollection'
import { useBlurNsfw } from 'src/services/helpers/preference'
import { cx } from 'src/utils/cx'
import { shouldMediaBeBlurred } from 'src/utils/media'
import { track } from 'src/utils/tracker'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { PreviewMask } from '../Artwork/BaseArtworkItemPreviewCard'
import MediaPreview from '../Common/MediaPreview'
import { MioExpression, MioImage } from '../Common/MioImage'
import UploadImage from '../Common/UploadImage'
import CursorPagingButton from '../CursorPaging/CursorPagingButton'
import { UIButton } from '../ui/Button'
import { UICheckbox } from '../ui/Checkbox'
import { UIInput } from '../ui/TextField'
import { useUserProfileContext } from './UserProfile.context'

const bookmarkSkeletons = Array.from({ length: 3 }, () => Math.random() * 5 + 6)

export type CollectionListProps = {
  userId?: string
  username?: string
  currentBookmarkId?: string
}

export type CollectionListRef = {
  reload: (backToFirstPage?: boolean) => void
}

export const CollectionList = forwardRef<
  CollectionListRef,
  CollectionListProps
>(function CollectionList({ userId, username, currentBookmarkId }, ref) {
  const [{ before, after }, setCursor] = useState<{
    before?: string
    after?: string
  }>({})
  const {
    collections,
    pageInfo,
    loading: loadingCollections,
    reload: reloadCollections,
  } = useCollections({ userId, before, after })

  const editDialogRef = useRef<CollectionEditDialogRef>(null)

  const navigate = useNavigate()

  const selectCollection = useCallback(
    (collection: Bookmark) => {
      track('collection_click', { collectionId: collection.id })
      if (currentBookmarkId === collection.id) {
        navigate(`/@${username}/artworks`)
      } else {
        navigate(`/@${username}/collection/${collection.id}`)
      }
    },
    [currentBookmarkId, username, navigate],
  )

  useImperativeHandle(ref, () => ({
    reload: (backToFirstPage = false) => {
      if (backToFirstPage) {
        setCursor({})
      }
      reloadCollections()
    },
  }))

  const { t } = useTranslation(['user', 'action'])

  if (loadingCollections && collections.length === 0) {
    return (
      <div className={cx('flex', 'gap-4')}>
        {bookmarkSkeletons.map(key => (
          <div
            className={cx('bg-skeleton animate-pulse rounded-md', 'w-36 h-36')}
            key={key}
          />
        ))}
      </div>
    )
  }

  const shouldShowPagingBtn = pageInfo?.hasNextPage || pageInfo?.hasPreviousPage

  return (
    <>
      <div className="grid gap-4 grid-cols-2 msm:grid-cols-3 mmd:grid-cols-4 mlg:grid-cols-6">
        {collections.map(bookmark => {
          return (
            <MemoizedCollectionCard
              key={bookmark.id}
              bookmark={bookmark}
              href={`/@${username}/collection/${bookmark.id}`}
              isSelected={bookmark.id === currentBookmarkId}
              onClick={selectCollection}
              onClickEdit={collection =>
                editDialogRef.current?.open(collection)
              }
              onClickDelete={async collection => {
                const toProceed = await UIDialog.confirm({
                  heading: t('user:collection.remove.title'),
                  content: t('user:collection.remove.content'),
                })
                if (toProceed) {
                  try {
                    // TODO: Show busy state
                    await removeCollection(collection.id)
                    // TODO: Exit collection view if the current collection was deleted
                    reloadCollections()
                    toast.success(t('user:collection.notice.delete.success'))
                  } catch {
                    toast.error(t('user:collection.notice.delete.failed'))
                  }
                }
              }}
            />
          )
        })}
      </div>
      {shouldShowPagingBtn && (
        <CursorPagingButton
          className="my-0"
          pageInfo={pageInfo}
          onChange={setCursor}
          isPosSeq={false}
        />
      )}

      <CollectionEditDialog
        ref={editDialogRef}
        i18nNamespace="collection"
        onSubmit={() => reloadCollections()}
      />
    </>
  )
})

function CollectionCard({
  bookmark,
  href,
  isSelected,
  onClick,
  onClickEdit,
  onClickDelete,
}: {
  bookmark: Bookmark
  href: string
  isSelected: boolean
  onClick: (bookmark: Bookmark) => void
  onClickEdit: (bookmark: Bookmark) => void
  onClickDelete: (bookmark: Bookmark) => void
}) {
  const { t } = useTranslation(['artwork', 'user'])
  const [blurNsfw] = useBlurNsfw()
  const [anchorEl, setAnchorEl] = useState<HTMLElement>()
  const isDefault = isDefaultCollection(bookmark.name)
  const collectionName = isDefault
    ? t('user:collection.default.name')
    : bookmark.name
  const shouldBlur = shouldMediaBeBlurred(bookmark.coverMedia)
  const nsfwBlur = shouldBlur && blurNsfw
  const { isMyProfile } = useUserProfileContext()

  return (
    <article
      className={cx(
        'aspect-square relative rounded-lg',
        isSelected &&
          'outline outline-theme-primary outline-2 outline-offset-1',
      )}
    >
      <Link
        to={href}
        className="size-full grid grid-rows-2 grid-cols-1 place-items-stretch rounded-lg relative overflow-hidden"
        onClick={ev => {
          ev.preventDefault()
          onClick(bookmark)
        }}
      >
        <figure className="contents">
          {bookmark.coverMedia ? (
            <>
              <MediaPreview
                className={cx(
                  'row-span-full col-span-full bg-cover bg-top',
                  nsfwBlur && 'blur-xl',
                )}
                mediaId={bookmark.coverMediaId}
                thumbnail={true}
                alt={collectionName}
              />
              <PreviewMask nsfwBlur={nsfwBlur} showExplicitIcon={nsfwBlur} />
            </>
          ) : (
            <MioImage
              expression={MioExpression.Happy}
              className="row-span-full col-span-full object-cover object-top"
            />
          )}
          <figcaption className="row-start-2 col-span-full relative p-2 flex flex-col justify-end items-center bg-gradient-to-t from-black/70 text-white text-shadow shadow-black text-balance">
            <p className="text-sm font-semibold line-clamp-1">
              {collectionName}
            </p>
            {/* <p className="text-sm opacity-80">{bookmark.count} images</p> */}
          </figcaption>
        </figure>
      </Link>
      {isMyProfile && (
        <div className="absolute top-1 right-1 bg-gray-200 rounded-md shadow ring-1 ring-black/10">
          <IconButton
            size="small"
            onClick={ev => setAnchorEl(ev.currentTarget)}
          >
            <IcBaselineMoreVert className="text-black" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={() => setAnchorEl(undefined)}
            onClick={() => {
              track('collection_add_cover_click')
            }}
          >
            <MenuItem
              onClick={() => {
                onClickEdit(bookmark)
                setAnchorEl(undefined)
                track('collection_edit_click')
              }}
            >
              <span className="text-primary">{t('artwork:action.edit')}</span>
            </MenuItem>
            {isDefault ? null : (
              <MenuItem
                onClick={() => {
                  onClickDelete(bookmark)
                  setAnchorEl(undefined)
                  track('collection_delete_click')
                }}
              >
                <span className="text-red-600">
                  {t('artwork:action.delete')}
                </span>
              </MenuItem>
            )}
          </Menu>
        </div>
      )}
    </article>
  )
}

const MemoizedCollectionCard = memo(CollectionCard)

export function CreateCollectionButton({
  type,
  i18nNamespace: prefix,
  onSubmit,
}: {
  type?: string
  i18nNamespace: string
  onSubmit: (bookmark: Bookmark) => void
}) {
  const editDialogRef = useRef<CollectionEditDialogRef>(null)
  const { t } = useTranslation(['user'])

  return (
    <>
      <UIButton
        variant="outlined"
        size="small"
        icon={<FluentAdd20Regular className="size-5" />}
        className="h-8 rounded-md shadow-none"
        onPress={() => {
          track('collection_create_click')
          editDialogRef.current?.open()
        }}
      >
        {t(`user:${prefix}.create.title`)}
      </UIButton>
      <CollectionEditDialog
        ref={editDialogRef}
        onSubmit={onSubmit}
        type={type}
        i18nNamespace={prefix}
      />
    </>
  )
}

export function AddToCollectionButton(props: {
  loading: boolean
  userId?: string
  selectedItems?: string[]
  addItemsToCollection: (bookmark: Bookmark) => void
}) {
  const { t } = useTranslation(['user'])

  const [anchorEl, setAnchorEl] = useState<Element>()
  // FIXME: Does not support more than 20 items
  const { collections } = useCollections({ userId: props.userId })

  return (
    <>
      <UIButton
        busy={props.loading}
        variant="filled"
        size="large"
        onPress={e => setAnchorEl(e.target)}
        disabled={(props.selectedItems ?? []).length === 0}
      >
        {t('user:collection.add.to')}
      </UIButton>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(undefined)}
      >
        {collections.map(bookmark => {
          return (
            <MenuItem
              key={bookmark.id}
              onClick={() => {
                setAnchorEl(undefined)
                props.addItemsToCollection(bookmark)
              }}
            >
              {isDefaultCollection(bookmark.name)
                ? t('user:collection.default.name')
                : bookmark.name}
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

/**
 * ==== Create/Update Collection ===============================================
 */

type CollectionEditDialogProps = {
  type?: string
  i18nNamespace: string
  onSubmit?: (collection: Bookmark) => void
}

export type CollectionEditDialogRef = {
  open: (collection?: Partial<Bookmark>) => void
  close: () => void
}

function defaultCollectionForm() {
  return {
    id: '',
    name: '',
    coverMedia: undefined as Media | undefined,
    isPublic: true,
  }
}

export const CollectionEditDialog = forwardRef<
  CollectionEditDialogRef,
  CollectionEditDialogProps
>(function CollectionEditDialog(props, ref) {
  const prefix = props.i18nNamespace

  const [open, setOpen] = useState(false)

  const [form, updateForm] = useSetState(defaultCollectionForm())
  const isDefault = isDefaultCollection(form.name)

  useImperativeHandle(ref, () => ({
    open: (formInit = defaultCollectionForm()) => {
      updateForm(formInit)
      setOpen(true)
    },
    close: () => setOpen(false),
  }))

  const { t } = useTranslation(['user', 'action'])

  const [submitting, submit] = useLoadingCallback(
    async ({
      name,
      coverMediaId,
      isPublic,
    }: {
      name?: string
      coverMediaId?: string
      isPublic?: boolean
    }) => {
      try {
        const newCollection = form.id
          ? await updateCollection(form.id, {
              coverMediaId,
              isPublic,
              ...(isDefault ? {} : { name }),
            })
          : await createCollection({
              name,
              coverMediaId,
              isPublic,
              type: props.type,
            })
        if (!newCollection) return

        props.onSubmit?.(newCollection)
        toast.success(
          form.id
            ? t(`user:${prefix}.notice.update.success`)
            : t(`user:${prefix}.notice.add.success`),
        )
        setOpen(false)
      } catch {
        toast.error(
          form.id
            ? t(`user:${prefix}.notice.update.failed`)
            : t(`user:${prefix}.notice.add.failed`),
        )
      }
    },
    [form.id, isDefault, props, t],
  )

  return (
    <UIDialog open={open} onClose={() => setOpen(false)}>
      <UIDialogHeader
        heading={
          form.id
            ? t(`user:${prefix}.edit.title`)
            : t(`user:${prefix}.create.title`)
        }
      />
      <UIDialogContent className="flex flex-col gap-4">
        <label className="text-sm font-medium opacity-70">
          {t(`user:${prefix}.cover.title`)}
        </label>
        <UploadImage
          value={form.coverMedia}
          onChange={coverMedia => {
            if (coverMedia) {
              updateForm({ coverMedia })
            }
          }}
          placeholderElement={<UploadImagePlacerholder />}
        />
        <label className="text-sm font-medium opacity-70">
          {t(`user:${prefix}.name`)}
        </label>
        <UIInput
          id="code-input"
          value={
            isDefault ? String(t(`user:${prefix}.default.name`)) : form.name
          }
          onChange={(ev: ChangeEvent<HTMLInputElement>) =>
            updateForm({ name: ev.target.value })
          }
          disabled={isDefault}
          autoComplete="off"
          placeholder={String(t(`user:${prefix}.create.placeholder`))}
          className="w-full mt-0"
          variant="rounded-normal"
        />
        <UICheckbox
          isSelected={form.isPublic}
          onChange={isPublic => {
            updateForm({ isPublic })
          }}
        >
          {t('common:visibility.public')}
        </UICheckbox>
      </UIDialogContent>
      <UIDialogFooter>
        <UIButton onPress={() => setOpen(false)}>{t('action:cancel')}</UIButton>
        <UIButton
          disabled={!form.name}
          busy={submitting}
          onPress={() => {
            submit({
              name: form.name,
              coverMediaId: form.coverMedia?.id,
              isPublic: form.isPublic,
            })
            track('collection_create', {
              type: form.id ? 'edit' : 'create',
            })
          }}
          variant="filled"
        >
          {form.id ? t('action:update') : t('action:create')}
        </UIButton>
      </UIDialogFooter>
    </UIDialog>
  )
})
